import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Mail, ArrowLeft, Shield, CheckCircle } from "lucide-react";
import {
  requestPasswordResetSchema,
  RequestPasswordResetFormData,
} from "@/lib/validators/auth";
import { useRequestPasswordReset } from "./hooks/useAuth";
import { Link } from "react-router-dom";
import { LOGOPATH } from "@/lib/paths";

export default function RequestPasswordResetPage() {
  const [emailSent, setEmailSent] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState("");
  const { mutate: requestReset, isPending } = useRequestPasswordReset();

  const form = useForm<RequestPasswordResetFormData>({
    resolver: zodResolver(requestPasswordResetSchema),
    defaultValues: { email: "" },
  });

  const onSubmit = (data: RequestPasswordResetFormData) => {
    requestReset(data, {
      onSuccess: () => {
        setSubmittedEmail(data.email);
        setEmailSent(true);
      },
    });
  };

  return (
    <div className="min-h-screen bg-white flex">
      {/* App Name - Top Left */}
      <div className="absolute top-8 left-8 z-20">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 flex items-center justify-center overflow-hidden">
            <img
              src={LOGOPATH}
              alt="Logo"
              className="w-10 h-10 object-contain"
            />
          </div>
          <h1 className="text-2xl font-bold text-slate-800">RoboBank</h1>
        </div>
      </div>

      {/* Left Column - Vector Image */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-gradient-to-br from-indigo-50 via-indigo-100/50 to-white">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-600/5 via-transparent to-indigo-400/5"></div>
        <div className="flex items-center justify-center w-full p-12">
          <div className="w-full max-w-lg h-96 bg-indigo-100/30 rounded-2xl flex items-center justify-center border border-indigo-200/50">
            <div className="text-center">
              <div className="w-24 h-24 bg-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                {emailSent ? (
                  <CheckCircle className="w-12 h-12 text-white" />
                ) : (
                  <Shield className="w-12 h-12 text-white" />
                )}
              </div>
              <p className="text-indigo-600 font-medium">
                {emailSent
                  ? "Email Sent Successfully"
                  : "Secure Password Recovery"}
              </p>
              <p className="text-sm text-indigo-400 mt-2">
                {emailSent
                  ? "Check your inbox for the reset link"
                  : "Your account security is our priority"}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Column - Form or Success */}
      <div className="flex-1 lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {emailSent ? (
            /* Success State */
            <>
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="text-3xl font-bold text-slate-800 mb-2">
                  Check Your Email
                </h2>
                <p className="text-slate-600 mb-2">
                  We've sent a password reset link to:
                </p>
                <p className="text-slate-800 font-medium text-lg">
                  {submittedEmail}
                </p>
                <p className="text-slate-500 text-sm mt-4">
                  Click the link in your email to reset your password. The link
                  will expire in 60 minutes.
                </p>
              </div>

              <div className="space-y-4">
                <Button
                  onClick={() => setEmailSent(false)}
                  variant="outline"
                  className="w-full h-12 border-slate-200 hover:bg-slate-50 text-slate-700 font-medium rounded-lg"
                >
                  Send to Different Email
                </Button>

                <Button
                  asChild
                  className="w-full h-12 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg"
                >
                  <Link to="/login">Back to Login</Link>
                </Button>
              </div>

              <div className="mt-6 text-center">
                <p className="text-xs text-slate-500">
                  Didn't receive the email? Check your spam folder or contact
                  support.
                </p>
              </div>
            </>
          ) : (
            /* Request Form State */
            <>
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-3xl font-bold text-slate-800 mb-2">
                  Forgot Password?
                </h2>
                <p className="text-slate-600">
                  Enter your email and we'll send you a link to reset your
                  password.
                </p>
              </div>

              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="space-y-6"
                >
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-slate-700">
                          Email Address
                        </FormLabel>
                        <FormControl>
                          <Input
                            placeholder="admin@robobank.com"
                            className="h-12 border-slate-200 focus:border-indigo-500 focus:ring-indigo-500 bg-white"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button
                    type="submit"
                    disabled={isPending}
                    className="w-full h-12 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
                  >
                    {isPending ? (
                      "Sending Reset Link..."
                    ) : (
                      <>
                        <Mail className="w-5 h-5 mr-2" />
                        Send Reset Link
                      </>
                    )}
                  </Button>
                </form>
              </Form>

              <div className="mt-8 text-center space-y-4">
                <p className="text-sm text-slate-600">
                  Remember your password?{" "}
                  <Link
                    to="/login"
                    className="text-indigo-600 hover:text-indigo-500 font-medium"
                  >
                    Sign In
                  </Link>
                </p>

                <Link
                  to="/login"
                  className="inline-flex items-center text-sm text-slate-500 hover:text-slate-700"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Back to Login
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
